// Discuss a stance with the twin, then confirm/correct it. Extracted from the
// (now-retired) Vault AlignmentsView so the one capability it had — talking a
// stance through before a verdict — lives on inside Align. Mirrors the
// document-discussion pattern.

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useTwinName } from '../lib/twin.js';

export interface DiscussStance {
  id: string;
  statement: string;
  predicted: boolean | null;
  confidence: number | null;
  reasoning: string | null;
  comment: string | null;
  verdict: boolean | null;
  mlPYes?: number | null;
  calibratedConf?: number | null;
}
interface Msg { id: string; role: string; content: string; createdAt: number }

export function DiscussStanceModal({ analysis, onClose }: { analysis: DiscussStance; onClose: () => void }) {
  const qc = useQueryClient();
  const twinName = useTwinName();
  const [convoId, setConvoId] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [nuance, setNuance] = useState(analysis.comment ?? '');
  const endRef = useRef<HTMLDivElement>(null);

  // Open (or resume) the discussion once.
  useEffect(() => {
    let alive = true;
    api<{ conversationId: string }>(`/api/analysis/${analysis.id}/discuss`, { method: 'POST', body: JSON.stringify({ language: 'fr' }) })
      .then((r) => { if (alive) setConvoId(r.conversationId); })
      .catch(() => {});
    return () => { alive = false; };
  }, [analysis.id]);

  const msgs = useQuery<{ messages: Msg[] }>({
    queryKey: ['conversation', convoId, 'messages'],
    queryFn: () => api(`/api/conversation/${convoId}`),
    enabled: Boolean(convoId),
    refetchOnWindowFocus: false,
  });
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs.data?.messages.length]);

  const send = useMutation({
    mutationFn: (content: string) => api('/api/conversation/send', { method: 'POST', body: JSON.stringify({ conversationId: convoId, content }) }),
    onSuccess: () => { setText(''); qc.invalidateQueries({ queryKey: ['conversation', convoId, 'messages'] }); },
  });

  const verdict = useMutation({
    mutationFn: (v: boolean) => api(`/api/analysis/${analysis.id}/verdict`, { method: 'POST', body: JSON.stringify({ verdict: v, comment: nuance.trim() || null }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['analysis'] }); onClose(); },
  });

  return (
    <div className="fixed inset-0 z-[130] grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[88vh] overflow-hidden flex flex-col rounded-2xl border border-ink/15 bg-void" onClick={(e) => e.stopPropagation()}>
        {/* Header — the stance */}
        <div className="px-5 py-3 border-b border-ink/10">
          <div className="flex items-start justify-between gap-3">
            <p className="font-serif text-[17px] text-ink leading-snug">{analysis.statement}</p>
            <button onClick={onClose} className="text-[11.5px] text-ink/50 hover:text-ink px-2 py-1 flex-shrink-0">close ✕</button>
          </div>
          <div className="flex items-center gap-3 mt-1.5 text-[11px] flex-wrap">
            <span className={analysis.predicted ? 'text-emerald-300/80' : 'text-rose-300/80'}>LLM : {analysis.predicted ? 'oui' : 'non'}</span>
            {analysis.confidence != null && <span className="text-ink/40">{analysis.confidence}% confiant{analysis.calibratedConf != null ? ` · ${analysis.calibratedConf}% calibré` : ''}</span>}
            {analysis.mlPYes != null && (
              <span className="text-sky-300/80">modèle ML : {analysis.mlPYes >= 0.5 ? 'oui' : 'non'} ({Math.round(analysis.mlPYes * 100)}%)</span>
            )}
            {analysis.mlPYes != null && analysis.predicted != null && (analysis.mlPYes >= 0.5) !== analysis.predicted && (
              <span className="text-violet-300 border border-violet-400/40 rounded-full px-1.5">désaccord LLM ↔ modèle</span>
            )}
          </div>
          {analysis.reasoning && <p className="font-serif italic text-[13px] text-ink/55 mt-1.5">“{analysis.reasoning}”</p>}
        </div>

        {/* Discussion */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-3">
          {!convoId || msgs.isLoading ? <div className="text-ink/40 text-[12px] py-4">Ouverture de la discussion…</div>
            : (msgs.data?.messages ?? []).filter((m) => m.role !== 'system').map((m) => {
              const visitor = m.role === 'visitor';
              return (
                <div key={m.id} className={`flex flex-col ${visitor ? 'items-end' : 'items-start'}`}>
                  <span className="text-[10px] text-ink/30 mb-0.5">{visitor ? 'toi' : twinName}</span>
                  <div className={`max-w-[85%] px-3.5 py-2 rounded-2xl font-serif text-[14px] leading-relaxed whitespace-pre-wrap ${
                    visitor ? 'bg-ink text-void rounded-br-sm' : 'bg-amber/[0.07] text-ink border border-amber/15 rounded-bl-sm'
                  }`}>{m.content}</div>
                </div>
              );
            })}
          <div ref={endRef} />
        </div>

        {/* Composer */}
        <div className="px-5 py-2.5 border-t border-ink/10 flex items-end gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && text.trim() && convoId) send.mutate(text); }}
            placeholder="Explique, corrige, nuance…" disabled={!convoId || send.isPending}
            className="flex-1 px-3.5 py-2 rounded-xl border border-ink/15 bg-white/[0.02] font-serif text-[15px] text-ink focus:border-ink/40 focus:outline-none" />
          <button onClick={() => convoId && text.trim() && send.mutate(text)} disabled={!convoId || !text.trim() || send.isPending}
            className="px-4 py-2 rounded-full bg-ink text-void text-[11.5px] disabled:opacity-30">{send.isPending ? '…' : 'Envoyer'}</button>
        </div>

        {/* Verdict — the takeaway that trains the twin */}
        <div className="px-5 py-3 border-t border-ink/10 bg-white/[0.015]">
          <input value={nuance} onChange={(e) => setNuance(e.target.value)} placeholder="Ta nuance / conclusion (optionnel) — enregistrée avec le verdict"
            className="w-full mb-2 px-3 py-2 rounded-lg border border-ink/10 bg-white/[0.02] font-serif text-[13.5px] text-ink focus:border-ink/40 focus:outline-none" />
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-ink/40 mr-1">Ta vraie réponse :</span>
            <button onClick={() => verdict.mutate(true)} disabled={verdict.isPending}
              className="px-4 py-1.5 rounded-full border border-emerald-300/40 text-emerald-300 text-[11.5px] hover:bg-emerald-300/10">Oui</button>
            <button onClick={() => verdict.mutate(false)} disabled={verdict.isPending}
              className="px-4 py-1.5 rounded-full border border-rose-300/40 text-rose-300 text-[11.5px] hover:bg-rose-300/10">Non</button>
            {analysis.verdict != null && <span className="text-[11px] text-ink/40">déjà confirmé · re-confirmer met à jour</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
